import { cn } from '@/utils/cn'
import { formatAmount } from '@vaultsfyi/common/utils'
import { forwardRef } from 'react'
import type { UseFormRegisterReturn } from 'react-hook-form'
import { formatUnits } from 'viem'
import { Button } from '../common/button'
import { Input, inputDecorationStyles } from '../common/input'
import { Label } from '../common/label'
import { Separator } from '../common/separator'
import { TokenImage } from '../common/tokenImage'
import { WalletIcon } from '../icons/wallet'

interface AmountInputProps extends Omit<UseFormRegisterReturn, 'ref'> {
  balance?: bigint
  decimals?: number
  usdValue?: string
  disabled?: boolean
  className?: string
  onMaxClick?: () => void
  error?: string
}

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  ({ balance, decimals = 6, usdValue, disabled, className, onMaxClick, error, ...register }, ref) => {
    const formattedBalance = balance !== undefined ? formatAmount(Number(formatUnits(balance, decimals))) : undefined

    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <div className="flex items-center justify-between">
          <Label htmlFor={register.name} className="font-medium text-body-foreground text-sm">
            Amount
          </Label>
          {formattedBalance && (
            <div className="flex items-center gap-1 text-sm">
              <WalletIcon size={16} />
              <span>{formattedBalance}</span>
              {onMaxClick && (
                <>
                  <Separator orientation="vertical" className="mx-1 h-3" />
                  <Button
                    variant="default"
                    size="default"
                    className="text-accent"
                    onClick={onMaxClick}
                    disabled={disabled}
                    type="button"
                  >
                    Max
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        <Label
          className={cn(
            inputDecorationStyles,
            'flex w-full cursor-text flex-col gap-1 px-3 py-2 md:px-4 md:py-3',
            'focus-within:border-focus focus-within:bg-input-hover hover:focus-within:border-focus',
            'has-[:focus-visible]:ring-2',
            disabled && 'cursor-not-allowed bg-input-disabled',
            error && 'border-destructive hocus-within:border-destructive'
          )}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <Input
              ref={ref}
              type="text"
              id={register.name}
              inputMode="decimal"
              placeholder="0"
              disabled={disabled}
              className={cn(
                'h-8 w-full rounded-none border-none bg-transparent p-0',
                'text-2xl text-active-foreground',
                '!ring-0',
                'hocus:bg-transparent',
                'disabled:bg-transparent'
              )}
              {...register}
            />
            <div className="flex items-center gap-2">
              <TokenImage symbol="USDC" className="size-8 min-w-8 p-0" />
              <span className="font-medium text-heading-foreground text-lg">USDC</span>
            </div>
          </div>
          {usdValue && <div className="text-sm">{usdValue}</div>}
        </Label>
        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
    )
  }
)

AmountInput.displayName = 'AmountInput'
